import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";
import { linkify } from "@/lib/linkify";
import SdmLogo from "@/components/SdmLogo";
import ChatForm from "@/components/ChatForm";
import axios from "axios";
import { useLang } from "@/contexts/LanguageContext";
import {
  PaperPlaneRight, Robot, User as UserIcon, Sparkle,
  Car, Calculator, Wrench, Package, ChatCircle, CalendarBlank,
  Storefront, Motorcycle, Handshake, ShieldCheck, Globe, Star,
  Download, File as FileIcon,
} from "@phosphor-icons/react";

// Public renderer for agent attachments — anonymous (uses public /files/{id}/meta).
function PublicAttachment({ url }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = url.startsWith("http") ? url : `${backend}${url}`;
  const id = url.split("/").pop();
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API_BASE}/files/${id}/meta`)
      .then((r) => { if (alive) setMeta(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);
  const isImage = meta?.content_type?.startsWith("image/");
  if (isImage) {
    return (
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-md overflow-hidden border border-border max-w-[260px] hover:opacity-90 transition-opacity"
        data-testid={`attachment-img-${id}`}
      >
        <img src={fullUrl} alt={meta?.filename || "adjunto"} className="w-full h-auto object-cover" />
      </a>
    );
  }
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background/60 hover:bg-background transition-colors max-w-[280px]"
      data-testid={`attachment-file-${id}`}
    >
      <FileIcon size={22} weight="fill" className="text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{meta?.filename || "Adjunto"}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {meta?.kind || "archivo"} · {meta ? Math.round((meta.size_bytes || 0) / 1024) : "…"} KB
        </div>
      </div>
      <Download size={14} className="text-muted-foreground shrink-0" />
    </a>
  );
}

const ICON_MAP = {
  Sparkle, Car, Calculator, Wrench, Package,
  ChatCircle, CalendarBlank, Storefront, Motorcycle, Handshake,
};

const FALLBACK_CHIPS = [
  { id: "nuevos", icon: "Sparkle", title: "Vehículos nuevos", subtitle: "Cadillac, Chevrolet, Infiniti, Nissan, Suzuki, Yamaha", prompt: "Hola, quiero ver los vehículos nuevos disponibles." },
  { id: "seminuevos", icon: "Car", title: "Seminuevos certificados", subtitle: "Multimarca, precios en USD", prompt: "Hola, quiero ver el inventario de seminuevos certificados." },
  { id: "cotizar", icon: "Calculator", title: "Cotizar cuota", subtitle: "Financiamiento SDM", prompt: "Quiero cotizar una cuota de financiamiento." },
  { id: "cita-taller", icon: "Wrench", title: "Cita de Taller", subtitle: "Mantenimiento, reparación, garantía", prompt: "Necesito agendar una cita de taller." },
  { id: "repuestos", icon: "Package", title: "Repuestos y Accesorios", subtitle: "Piezas originales, alternativas y accesorios", prompt: "Estoy buscando un repuesto o accesorio para mi vehículo." },
];

function WhatsAppOnly() {
  const [link, setLink] = useState(null);
  useEffect(() => {
    document.title = "Santo Domingo Motors · WhatsApp";
    axios.get(`${API_BASE}/public/whatsapp-number`).then((r) => setLink(r.data?.wa_link)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="whatsapp-only-page">
      <div className="max-w-sm text-center space-y-4">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#25D366] flex items-center justify-center text-white text-3xl">✆</div>
        <h1 className="font-heading text-2xl font-bold">Escríbenos por WhatsApp</h1>
        <p className="text-sm text-muted-foreground">Nuestro Asistente Virtual de Santo Domingo Motors te atiende por WhatsApp.</p>
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-block rounded-full bg-[#25D366] px-6 py-3 text-white font-semibold" data-testid="whatsapp-only-link">
            Abrir WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

export default function PublicChat() {
  const p = new URLSearchParams(window.location.search);
  // Web chat retired: /chat only serves the CSAT survey link (?conv=…&csat=1).
  if (!(p.get("conv") && p.get("csat"))) return <WhatsAppOnly />;
  return <PublicChatInner />;
}

function PublicChatInner() {
  const { lang, setLang } = useLang();
  const [messages, setMessages] = useState([
    {
      role: "ai",
      content: lang === "es"
        ? "Hola 👋, soy tu Asistente Virtual Santo Domingo Motors\nEstoy aquí para asistirte con todo lo que necesites."
        : "Hi 👋, I'm your Santo Domingo Motors Virtual Assistant\nI'm here to help you with anything you need.",
    },
  ]);
  const [convId, setConvId] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [guidedDismissed, setGuidedDismissed] = useState(false);
  const [chips, setChips] = useState(FALLBACK_CHIPS);
  const [csatOpen, setCsatOpen] = useState(false);
  const [csatSent, setCsatSent] = useState(false);
  const [csatComment, setCsatComment] = useState("");
  const [csatConfig, setCsatConfig] = useState(null);
  const [csatAnswers, setCsatAnswers] = useState({}); // {question_id: score}
  const scrollRef = useRef(null);

  useEffect(() => {
    document.title = "SDM Concierge · Chat";
    let alive = true;
    axios.get(`${API_BASE}/settings/widget-chips`)
      .then((r) => {
        if (!alive) return;
        const remote = (r.data?.chips || []).filter((c) => c.enabled !== false);
        if (remote.length > 0) setChips(remote);
      })
      .catch(() => {});
    // Load editable CSAT config (bilingual questions, comment prompt, thank-you)
    axios.get(`${API_BASE}/csat/config`)
      .then((r) => { if (alive) setCsatConfig(r.data); })
      .catch(() => {});

    // Sprint 35C — Honor ?conv=<id>&csat=1 from the farewell SMS/WhatsApp link.
    // The agent-side resolve flow builds the URL `/chat?conv=<cid>&csat=1`;
    // without this handler, clicking the link just loaded the empty widget and
    // the CSAT survey never opened. Also fetch the conversation history so the
    // customer sees the thread they just rated.
    try {
      const params = new URLSearchParams(window.location.search);
      const urlConv = params.get("conv");
      const urlCsat = params.get("csat");
      if (urlConv) {
        setConvId(urlConv);
        // Pull existing thread so the user has context above the CSAT modal.
        axios.get(`${API_BASE}/public/conversations/${urlConv}/messages`)
          .then((r) => {
            if (!alive) return;
            const hist = (r.data?.messages || []).map((m) => ({
              id: m.id,
              role: m.sender_type === "agent"
                ? "agent"
                : m.sender_type === "system"
                  ? "system"
                  : m.sender_type === "customer"
                    ? "user"
                    : "ai",
              content: m.content,
              sender_name: m.sender_name,
              attachments: m.attachments || [],
            }));
            if (hist.length > 0) setMessages(hist);
          })
          .catch(() => {});
      }
      if (urlCsat === "1") {
        setCsatOpen(true);
      }
    } catch (_) { /* no window in SSR */ }

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const showGuided = !guidedDismissed && !convId && messages.length === 1;

  // Auto-open CSAT survey after 90s of inactivity following ≥2 AI replies (public demo only)
  const aiReplies = messages.filter((m) => m.role === "ai" && m.content).length;
  useEffect(() => {
    if (csatSent || csatOpen || aiReplies < 2 || !convId) return;
    const timer = setTimeout(() => setCsatOpen(true), 90 * 1000);
    return () => clearTimeout(timer);
  }, [messages, csatSent, csatOpen, aiReplies, convId]);

  // Sprint 2 Feature 2 — poll for outbound agent messages every 6s.
  // Uses the message.created_at as `since` so the customer gets any human-agent
  // reply issued from the internal console in near-real time (no auth needed).
  const lastAiAgentAtRef = useRef(null);
  useEffect(() => {
    if (!convId) return;
    const iv = setInterval(async () => {
      try {
        const params = lastAiAgentAtRef.current ? `?since=${encodeURIComponent(lastAiAgentAtRef.current)}` : "";
        const res = await fetch(`${API_BASE}/public/conversations/${convId}/messages${params}`);
        if (!res.ok) return;
        const data = await res.json();
        // Auto-open CSAT when conversation is resolved/closed (server-side)
        const status = data.status;
        if (
          (status === "resolved" || status === "closed") &&
          !csatSent && !csatOpen &&
          csatConfig?.auto_trigger_on_close !== false
        ) {
          setCsatOpen(true);
        }
        const newMsgs = data.messages || [];
        if (newMsgs.length === 0) return;
        // Dedup by real message id (server-provided). Falls back to a content
        // fingerprint for legacy messages that seeded the local state.
        setMessages((cur) => {
          const knownIds = new Set(cur.map((m) => m.id).filter(Boolean));
          const filtered = newMsgs
            .filter((m) => !knownIds.has(m.id))
            .map((m) => ({
              id: m.id,
              role: m.sender_type === "agent"
                ? "agent"
                : m.sender_type === "system"
                  ? "system"
                  : "ai",
              content: m.content,
              sender_name: m.sender_name,
              attachments: m.attachments || [],
            }));
          if (filtered.length === 0) return cur;
          return [...cur, ...filtered];
        });
        lastAiAgentAtRef.current = newMsgs[newMsgs.length - 1].created_at;
      } catch (_) { /* silent */ }
    }, 6000);
    return () => clearInterval(iv);
  }, [convId, csatSent, csatOpen, csatConfig]);

  async function submitCsat() {
    const activeQs = (csatConfig?.questions || []).filter((q) => q.active);
    // Build answers payload (skip questions the user didn't rate)
    const answers = activeQs
      .map((q) => ({ question_id: q.id, score: Number(csatAnswers[q.id] || 0) }))
      .filter((a) => a.score >= 1 && a.score <= 5);
    if (!convId || answers.length === 0) return;
    try {
      await fetch(`${API_BASE}/csat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId, answers, comment: csatComment || null }),
      });
      setCsatSent(true);
      setCsatOpen(false);
    } catch (e) { /* silent */ }
  }

  const sendMessage = async (rawMsg) => {
    const userMsg = (rawMsg ?? input).trim();
    if (!userMsg || streaming) return;
    setInput("");
    setGuidedDismissed(true);
    setMessages((m) => [
      ...m,
      { role: "customer", content: userMsg },
      { role: "ai", content: "", pending: true },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/ai/chat/agent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: convId,
          customer_name: "Visitante Web",
          channel: "web",
          message: userMsg,
        }),
      });
      const data = await res.json();
      if (data.conversation_id) setConvId(data.conversation_id);
      // Push the polling cursor forward so the poller skips messages we
      // already received in-band (fixes the "AI replies twice" bug).
      if (data.message_created_at) lastAiAgentAtRef.current = data.message_created_at;
      // Detect request_customer_form tool call so we can inject the inline form
      // marker BEFORE the AI reply (the backend inserts a [[FORM:type]] system
      // message during tool execution, but the poller misses it because its
      // created_at is < message_created_at above).
      let formType = null;
      for (const tc of (data.tool_calls || [])) {
        if (tc.name === "request_customer_form") {
          let args = tc.arguments;
          if (typeof args === "string") { try { args = JSON.parse(args); } catch (_) { args = {}; } }
          formType = (args?.form_type) || null;
          break;
        }
      }
      setMessages((m) => {
        const arr = [...m];
        arr[arr.length - 1] = {
          id: data.message_id,
          role: "ai",
          content: data.reply || (lang === "es" ? "Sin respuesta." : "No reply."),
        };
        if (formType) {
          arr.splice(arr.length - 1, 0, { role: "system", content: `[[FORM:${formType}]]` });
        }
        return arr;
      });
    } catch (e) {
      setMessages((m) => {
        const arr = [...m];
        arr[arr.length - 1] = {
          role: "ai",
          content: lang === "es" ? "Error de conexión." : "Connection error.",
        };
        return arr;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="public-chat">
      {/* Slim brand bar */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md overflow-hidden bg-black flex items-center justify-center shrink-0">
              <SdmLogo size={34} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-sm sm:text-base leading-tight">
                Santo Domingo Motors
              </div>
              <div className="text-[10px] sm:text-[11px] text-muted-foreground flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {lang === "es" ? "Asistente Virtual" : "Virtual Assistant"}
              </div>
            </div>
          </div>
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            data-testid="lang-toggle"
            className="text-xs px-2 py-1 rounded-md border border-border hover:bg-muted flex items-center gap-1.5"
          >
            <Globe size={12} />
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>
      </header>

      {/* Chat area */}
      <main className="flex-1 flex flex-col max-w-4xl w-full mx-auto px-4 sm:px-6 py-4 sm:py-6 min-h-0">
        <div className="flex-1 border border-border rounded-lg bg-card overflow-hidden shadow-lg flex flex-col min-h-0">
          <div
            ref={scrollRef}
            className="flex-1 overflow-auto p-4 sm:p-5 space-y-3 bg-background/40 min-h-[60vh]"
          >
            {messages.map((m, i) => {
              if (m.role === "system") {
                // Detect form marker and render inline form instead of a plain separator.
                const formMatch = /^\[\[FORM:(appointment|quote|sales_lead|parts_quote|owner_transfer|tire_quote)\]\]$/.exec((m.content || "").trim());
                if (formMatch) {
                  return (
                    <ChatForm
                      key={i}
                      formType={formMatch[1]}
                      conversationId={convId}
                      onSubmitted={() => {/* server auto-inserts a follow-up system msg */}}
                    />
                  );
                }
                return (
                  <div key={i} className="flex items-center gap-3 my-2 px-2" data-testid="msg-system">
                    <div className="flex-1 h-px bg-border" />
                    <div className="text-[11px] text-muted-foreground italic whitespace-nowrap px-2 py-1 rounded-full bg-secondary/60 border border-border">
                      {m.content}
                    </div>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                );
              }
              return (
              <div
                key={i}
                className={`flex gap-2 ${m.role === "customer" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "ai" && (
                  <div className="w-8 h-8 rounded-md overflow-hidden bg-black flex items-center justify-center shrink-0 self-end">
                    <SdmLogo size={30} />
                  </div>
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[75%] rounded-lg p-3 text-sm ${
                    m.role === "customer"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : m.role === "agent"
                        ? "bg-emerald-500/10 border border-emerald-500/30 rounded-bl-sm"
                        : "bg-secondary rounded-bl-sm"
                  }`}
                  data-testid={`msg-${m.role}`}
                >
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-1">
                    {m.role === "customer" ? (
                      <UserIcon size={10} weight="fill" />
                    ) : m.role === "agent" ? (
                      <UserIcon size={10} weight="fill" className="text-emerald-600" />
                    ) : null}
                    {m.role === "customer"
                      ? (lang === "es" ? "Tú" : "You")
                      : m.role === "agent"
                        ? `${m.sender_name || (lang === "es" ? "Asesor SDM" : "SDM Advisor")}`
                        : (lang === "es" ? "Asistente Virtual SDM" : "SDM Virtual Assistant")}
                  </div>
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.content
                      ? linkify(m.content.replace(/\[\[FORM:(appointment|quote|sales_lead|parts_quote|owner_transfer|tire_quote)\]\]/gi, "").trim())
                      : (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1" data-testid="msg-attachments">
                      {m.attachments.map((url, k) => (
                        <PublicAttachment key={k} url={url} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {showGuided && (
              <div className="pt-2" data-testid="guided-panel">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3 flex items-center gap-2">
                  <Sparkle size={10} weight="fill" />
                  {lang === "es" ? "¿En qué te ayudamos hoy?" : "How can we help today?"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {chips.map((chip) => {
                    const Icon = ICON_MAP[chip.icon] || Sparkle;
                    return (
                      <button
                        key={chip.id}
                        onClick={() => sendMessage(chip.prompt)}
                        disabled={streaming}
                        data-testid={`guided-${chip.id}`}
                        className="flex items-start gap-3 text-left p-3 rounded-lg border border-border bg-card hover:border-primary hover:bg-primary/5 transition-all group"
                      >
                        <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Icon size={18} weight="regular" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-heading font-semibold text-sm truncate">
                            {chip.title}
                          </div>
                          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                            {chip.subtitle}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setGuidedDismissed(true)}
                  data-testid="guided-skip"
                  className="mt-3 text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {lang === "es" ? "O escribe libremente abajo →" : "Or type freely below →"}
                </button>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-border p-3 bg-card">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder={lang === "es" ? "Escribe tu mensaje…" : "Type your message…"}
                disabled={streaming}
                data-testid="widget-input"
                className="flex-1"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={streaming}
                data-testid="widget-send"
                className="gap-1.5"
              >
                <PaperPlaneRight size={14} />
                {streaming ? "…" : (lang === "es" ? "Enviar" : "Send")}
              </Button>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <ShieldCheck size={10} />
                {lang === "es"
                  ? "Demo pública · Sin autenticación. Las conversaciones son registradas para pruebas."
                  : "Public demo · No login required. Conversations are logged for testing."}
              </span>
              <div className="flex items-center gap-3">
                {aiReplies >= 1 && convId && !csatSent && (
                  <button
                    onClick={() => setCsatOpen(true)}
                    data-testid="csat-open"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    <Star size={10} weight="fill" />
                    {lang === "es" ? "Califícanos" : "Rate us"}
                  </button>
                )}
                <a href="https://santodomingomotors.com.do" target="_blank" rel="noreferrer" className="hover:text-primary underline underline-offset-2">
                  santodomingomotors.com.do
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* CSAT modal — driven by editable csat_config */}
        {csatOpen && !csatSent && (() => {
          const activeQs = (csatConfig?.questions || [{ id: "overall", text_es: "¿Cómo estuvo tu experiencia?", text_en: "How was your experience?", subtitle_es: "Tu opinión nos ayuda a mejorar el SDM Concierge", subtitle_en: "Your feedback helps us improve SDM Concierge", active: true }])
            .filter((q) => q.active)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          const tt = (es, en) => (lang === "es" ? es : en);
          const commentPrompt = tt(
            csatConfig?.comment_prompt_es || "Cuéntanos más (opcional)…",
            csatConfig?.comment_prompt_en || "Tell us more (optional)…",
          );
          const canSubmit = activeQs.some((q) => Number(csatAnswers[q.id] || 0) >= 1);
          const showComment = csatConfig?.show_comment !== false;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="csat-modal">
              <div className="bg-card rounded-lg border border-border p-6 max-w-sm w-full shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="space-y-5">
                  {activeQs.map((q, qi) => {
                    const current = Number(csatAnswers[q.id] || 0);
                    return (
                      <div key={q.id || qi} data-testid={`csat-question-block-${qi}`}>
                        <div className="text-center mb-3">
                          <div className="font-heading font-bold text-lg">
                            {tt(q.text_es, q.text_en)}
                          </div>
                          {(q.subtitle_es || q.subtitle_en) && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {tt(q.subtitle_es, q.subtitle_en)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              onClick={() => setCsatAnswers((prev) => ({ ...prev, [q.id]: n }))}
                              data-testid={`csat-star-${qi}-${n}`}
                              className={`transition-transform hover:scale-110 ${n <= current ? "text-amber-400" : "text-muted-foreground/30"}`}
                            >
                              <Star size={32} weight={n <= current ? "fill" : "regular"} />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {showComment && (
                  <textarea
                    value={csatComment}
                    onChange={(e) => setCsatComment(e.target.value)}
                    placeholder={commentPrompt}
                    rows={2}
                    data-testid="csat-comment"
                    className="w-full text-xs rounded-md border border-input bg-background px-3 py-2 mb-3 mt-4 resize-none"
                  />
                )}
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="ghost" size="sm" onClick={() => setCsatOpen(false)} data-testid="csat-cancel">
                    {lang === "es" ? "Ahora no" : "Not now"}
                  </Button>
                  <Button size="sm" onClick={submitCsat} disabled={!canSubmit} data-testid="csat-submit">
                    {lang === "es" ? "Enviar" : "Submit"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CSAT thank you toast */}
        {csatSent && (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-card border border-border rounded-full px-4 py-2 shadow-lg text-xs flex items-center gap-2 animate-fade-in"
            data-testid="csat-thanks"
          >
            <Star size={14} weight="fill" className="text-amber-400" />
            {lang === "es"
              ? (csatConfig?.thank_you_es || "¡Gracias por tu opinión!")
              : (csatConfig?.thank_you_en || "Thanks for your feedback!")}
          </div>
        )}
      </main>
    </div>
  );
}
